import type { PropsWithChildren } from "react";
import { Navigate, useLocation } from "react-router-dom";

import { useActivePurchaseAccess } from "../../hooks/useActivePurchaseAccess";
import { useAuth } from "../../hooks/useAuth";
import { isOfficeVesselScopedPath } from "../../lib/officeVesselScope";
import { hasPermission } from "../../lib/permissions";

interface RequirePermissionProps extends PropsWithChildren {
  roles?: string[];
  forms?: string[];
  processes?: string[];
  anyOf?: Array<{
    roles?: string[];
    forms?: string[];
    processes?: string[];
  }>;
}

function resolveFallbackPath(userType: "office" | "crew" | undefined) {
  if (!userType) {
    return "/login";
  }

  return "/dashboard";
}

export function RequirePermission({ roles, forms, processes, anyOf, children }: RequirePermissionProps) {
  const { isInitialized, isLoading, user } = useAuth();
  const { user: activePurchaseUser, isLoading: isActivePurchaseAccessLoading } = useActivePurchaseAccess();
  const location = useLocation();
  const permissionUser = isOfficeVesselScopedPath(location.pathname) ? activePurchaseUser ?? user : user;

  if (!isInitialized || isLoading || (isOfficeVesselScopedPath(location.pathname) && isActivePurchaseAccessLoading)) {
    return <div>Loading...</div>;
  }

  const hasDirectPermission = hasPermission(permissionUser, { roles, forms, processes });
  const hasAnyConfiguredPermission = Boolean(anyOf?.some((option) => hasPermission(permissionUser, option)));

  if (!hasDirectPermission && !hasAnyConfiguredPermission) {
    return <Navigate replace to={resolveFallbackPath(user?.type)} />;
  }

  return <>{children}</>;
}
